import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Eye, EyeOff, Lock, AlertCircle, ShieldCheck } from 'lucide-react';
import { alterarSenhaCliente } from '../services/api';
import { useToast } from '../hooks/use-toast';

const PrimeiroAcesso = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const { userId, nome } = location.state || {};
  
  const [passwords, setPasswords] = useState({
    novaSenha: '',
    confirmarSenha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Se não tiver userId, redireciona para login
  if (!userId) {
    navigate('/login');
    return null;
  }

  const validatePasswords = () => {
    if (!passwords.novaSenha || passwords.novaSenha.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres');
      return false;
    }
    
    if (passwords.novaSenha !== passwords.confirmarSenha) {
      setError('As senhas não conferem');
      return false;
    }

    // Verifica se a nova senha é diferente do CPF/CNPJ
    const senhaLimpa = passwords.novaSenha.replace(/\D/g, '');
    if (senhaLimpa.length >= 11 && senhaLimpa.length <= 14) {
      setError('A nova senha não pode ser igual ao seu CPF ou CNPJ');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswords()) return;
    
    setLoading(true);
    setError('');
    
    try {
      await alterarSenhaCliente(userId, passwords.novaSenha);
      
      toast({
        title: "Senha alterada com sucesso!",
        description: "Agora você pode acessar o sistema normalmente.",
        duration: 5000,
      });
      
      navigate('/');
      
    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      setError(err.message || 'Erro ao alterar senha. Tente novamente.');
      
      toast({
        title: "Erro ao alterar senha",
        description: err.message || 'Erro ao alterar senha. Tente novamente.',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
            alt="Knorr Leilões"
            className="h-16 w-auto mx-auto mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>

        <Card className="border-2 border-yellow-400">
          <CardHeader className="text-center bg-yellow-500 text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-center space-x-2">
              <ShieldCheck className="h-5 w-5" />
              <span>Primeiro Acesso</span>
            </CardTitle>
          </CardHeader>

          <CardContent className="p-6">
            <div className="mb-6 text-center">
              <p className="text-gray-700 font-medium">
                Olá, {nome ? nome.split(' ')[0] : 'Usuário'}!
              </p>
              <p className="text-gray-600 text-sm mt-2">
                Por segurança, você precisa criar uma nova senha para continuar.
              </p>
            </div>

            <Alert className="mb-6 border-yellow-400 bg-yellow-50">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                Sua senha atual é temporária. Crie uma senha segura para proteger sua conta.
              </AlertDescription>
            </Alert>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="novaSenha" className="text-sm font-medium text-gray-700">
                  Nova Senha *
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="novaSenha"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 6 caracteres"
                    value={passwords.novaSenha}
                    onChange={(e) => {
                      setPasswords(prev => ({ ...prev, novaSenha: e.target.value }));
                      setError('');
                    }}
                    className="pl-10 pr-10 border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmarSenha" className="text-sm font-medium text-gray-700">
                  Confirmar Nova Senha *
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    id="confirmarSenha"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Repita a nova senha"
                    value={passwords.confirmarSenha}
                    onChange={(e) => {
                      setPasswords(prev => ({ ...prev, confirmarSenha: e.target.value }));
                      setError('');
                    }}
                    className="pl-10 pr-10 border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-600">{error}</AlertDescription>
                </Alert>
              )}

              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <p className="font-medium mb-2">Dicas para uma senha segura:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Use pelo menos 6 caracteres</li>
                  <li>Misture letras e números</li>
                  <li>Evite usar dados pessoais</li>
                </ul>
              </div>

              <Button
                type="submit"
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 transition-colors duration-200"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Alterando senha...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <ShieldCheck className="h-4 w-4" />
                    <span>Criar Nova Senha</span>
                  </div>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrimeiroAcesso;
